import { getAuthMe } from '../api/client';
import { listTargets } from '../api/targets';

// The orgs this browser has signed into, offered on the sign-in form so a
// returning user picks rather than retypes (ADR-0023 Amendment D). Per-browser
// and per-device, holding no credential: the org identifier is a routing hint,
// and VP alone enforces who may sign in.

const ORGS_KEY = 'tc:signed-in-orgs:v1';
const PENDING_KEY = 'tc:pending-org:v1';

const MAX_ORGS = 8;

export interface SignedInOrg {
  /** What the user typed — the value replayed to GET /api/auth/start?org=. */
  id: string;
  /** The tenant's display name, from the GET /api/targets home row. */
  name: string;
  lastUsedUtc: string;
}

export function listSignedInOrgs(): SignedInOrg[] {
  const raw = read(ORGS_KEY, localStorage);
  if (!raw) return [];

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // lastUsedUtc is ISO 8601 UTC, so a string sort is a chronological one.
    return parsed.filter(isSignedInOrg).sort((a, b) => b.lastUsedUtc.localeCompare(a.lastUsedUtc));
  } catch {
    return [];
  }
}

export function forgetSignedInOrg(id: string): void {
  const kept = listSignedInOrgs().filter(o => o.id !== id);
  write(ORGS_KEY, JSON.stringify(kept), localStorage);
}

/**
 * Hold the org across the VP round trip. The page unloads into VP on submit, so
 * this is the only place the typed value survives to the landing.
 */
export function stashPendingOrg(id: string): void {
  write(PENDING_KEY, id, sessionStorage);
}

/**
 * Promote the stashed org into the remembered list, once the browser has landed
 * back in the app authenticated. Runs once per full page load and returns
 * immediately when no sign-in is in flight.
 *
 * The id is the value the user typed, not one read back from the API: it is the
 * exact string GET /api/auth/start has just accepted, so replaying it always
 * resolves. /api/auth/me does carry a vpTenantId, but that names the *active*
 * target (UserContext.cs:31), which stops being the home org once a second
 * target is attached.
 */
export async function promotePendingOrg(): Promise<void> {
  const pending = read(PENDING_KEY, sessionStorage);
  if (!pending) return;

  try {
    await getAuthMe();
    const targets = await listTargets();
    // SessionTarget.HomeId — the sign-in identity's row, always first and always present.
    const home = targets.find(t => t.id === 'home');
    if (home) remember(pending, home.displayName);
  } catch {
    // A 401 here is a sign-in that never landed, so nothing is written. The
    // stash clears either way, so a later page load cannot resurrect it.
  } finally {
    clear(PENDING_KEY, sessionStorage);
  }
}

function remember(id: string, name: string): void {
  const others = listSignedInOrgs().filter(o => o.id !== id);
  const next = [{ id, name, lastUsedUtc: new Date().toISOString() }, ...others].slice(0, MAX_ORGS);
  write(ORGS_KEY, JSON.stringify(next), localStorage);
}

function isSignedInOrg(v: unknown): v is SignedInOrg {
  const o = v as SignedInOrg | null;
  return !!o && typeof o.id === 'string' && typeof o.name === 'string' && typeof o.lastUsedUtc === 'string';
}

function read(key: string, storage: Storage): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string, storage: Storage): void {
  try {
    storage.setItem(key, value);
  } catch {
    // Quota exceeded or private browsing. The list is a convenience; the
    // free-text field still signs the user in.
  }
}

function clear(key: string, storage: Storage): void {
  try {
    storage.removeItem(key);
  } catch {
    // As above.
  }
}
